import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useContext,
} from "react";
import { cofRequest } from "../../../../api/cof";
import { getOccupiedSlots } from "../../../../api/cof";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormFeedback,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQrcode } from "@fortawesome/free-solid-svg-icons";
import { Html5Qrcode } from "html5-qrcode";
import Swal from "sweetalert2";
import Select from "react-select";
import "./cofrequest.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AuthContext } from "../../../ExtraPages/AuthProvider";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "./firebase-config";
import axios from "axios";
import { getVesselsByOwner } from '../../../../api/VesselDetails/addVesselApi';

const COFRequest = forwardRef((props, ref) => {
  const today = new Date().toISOString().split('T')[0];
  const { toggleModal, inspectors, cofSubmitted } = props;
  const { user } = useContext(AuthContext);
  const [userId, setUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // QR Scanner State
  const [isScanning, setIsScanning] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('Checking camera...');
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [currentFishermanIndex, setCurrentFishermanIndex] = useState(null);
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const isScannerRunning = useRef(false);

  // Time Slots and Options
  const [timeSlots] = useState([
    '08:00 - 09:00',
    '09:00 - 10:00',
    '10:00 - 11:00',
    '11:00 - 12:00',
    '12:00 - 13:00',
    '13:00 - 14:00',
    '14:00 - 15:00',
    '15:00 - 16:00',
    '16:00 - 17:00',
  ]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([...timeSlots]);
  const [shouldDisable, setShouldDisable] = useState(true);
  const [shouldDisableTimeSlot, setShouldDisableTimeSlot] = useState(true);

  // Vessel and Harbour Options
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const fetchVessels = async () => {
      if (!userId) return;
      try {
        const response = await getVesselsByOwner(userId);
        console.log('Vessels fetched:', response);
        if (response && response.data && Array.isArray(response.data)) {
          const vesselOptions = response.data.map((vessel) => ({
            value: vessel.IMULA_Number,
            label: vessel.IMULA_Number,
          }));
          setOptions(vesselOptions);
        }
      } catch (error) {
        console.error('Error fetching vessels:', error);
      }
    };
    fetchVessels();
  }, [userId]);

  const harbours = [
    { value: 'Ambalangoda', label: 'Ambalangoda' },
    { value: 'Beruwala', label: 'Beruwala' },
    { value: 'Chilaw', label: 'Chilaw' },
    { value: 'Cod-Bay', label: 'Cod-Bay' },
    { value: 'Dikkowita North', label: 'Dikkowita North' },
    { value: 'Dikkowita South', label: 'Dikkowita South' },
    { value: 'Dodanduwa', label: 'Dodanduwa' },
    { value: 'Galle', label: 'Galle' },
    { value: 'Hambantota', label: 'Hambantota' },
    { value: 'Hikkaduwa', label: 'Hikkaduwa' },
    { value: 'Kalametiya', label: 'Kalametiya' },
    { value: 'Kalpitiya', label: 'Kalpitiya' },
    { value: 'Kirinda', label: 'Kirinda' },
    { value: 'Kudawella', label: 'Kudawella' },
    { value: 'Mirissa', label: 'Mirissa' },
    { value: 'Myliddy', label: 'Myliddy' },
    { value: 'Negombo', label: 'Negombo' },
    { value: 'Nilwella', label: 'Nilwella' },
    { value: 'Panadura', label: 'Panadura' },
    { value: 'Puranawella', label: 'Puranawella' },
    { value: 'Suduwella', label: 'Suduwella' },
    { value: 'Tangalle', label: 'Tangalle' },
    { value: 'Valachchanai', label: 'Valachchanai' },
    { value: 'Wellamankara', label: 'Wellamankara' },
  ];

  const officers = inspectors.map((inspector) => ({
    value: inspector.userId,
    label: `${inspector.firstName} ${inspector.lastName}`,
  }));

  // Formik Initialization
  const formik = useFormik({
    initialValues: {
      boatOwner: '',
      requestDate: '',
      timeSlot: '',
      vesselNumber: '',
      harbour: '',
      officer: '',
      officerName: '',
      fisherman1: '',
      fisherman2: '',
      fisherman3: '',
      fisherman1_id: '',
      fisherman2_id: '',
      fisherman3_id: '',
    },
    validationSchema: Yup.object({
      boatOwner: Yup.string().required('Boat owner is required'),
      requestDate: Yup.date()
        .required('Date is required')
        .min(today, 'Cannot select a past date'),
      timeSlot: Yup.string().required('Time Slot is required'),
      vesselNumber: Yup.string().required('Vessel selection is required'),
      harbour: Yup.string().required('Harbour selection is required'),
      officer: Yup.string().required('Officer selection is required'),
      fisherman1: Yup.string().required(
        'At least Fisherman 1 selection is required'
      ),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      //setCofSubmitted(true);
      const selectedOfficer = officers.find(
        (option) => option.value === values.officer
      );
      values.officerName = selectedOfficer ? selectedOfficer.label : '';
      try {
        await handleSubmit(values);
        formik.resetForm(); // Reset form after successful submission
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!user) return;
    setUserId(user.uid);
    formik.setFieldValue('boatOwner', user.uid);
  }, [user]);

  // QR Scanner Functions
  const stopScanner = async () => {
    if (scannerRef.current && isScannerRunning.current) {
      try {
        await scannerRef.current.stop();
        isScannerRunning.current = false;
        scannerRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  useEffect(() => {
    if (!isScanning) {
      setCameraStatus('Checking camera...');
      return;
    }

    const initializeCameras = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setCameraStatus('Camera ready');
        const devices = await Html5Qrcode.getCameras();
        setCameras(devices);
        if (devices.length > 0) {
          setSelectedCameraId(devices[0].id);
        } else {
          setCameraStatus('No cameras found');
          Swal.fire({
            icon: 'error',
            title: 'No Cameras',
            text: 'No cameras were detected on your device',
            confirmButtonText: 'OK',
          });
          setIsScanning(false);
        }
      } catch (err) {
        console.error('Camera error:', err);
        setCameraStatus('Camera access denied');
        Swal.fire({
          icon: 'error',
          title: 'Camera Error',
          text: 'Failed to access camera. Please check permissions.',
          confirmButtonText: 'OK',
        });
        setIsScanning(false);
      }
    };

    initializeCameras();

    return () => {
      stopScanner();
    };
  }, [isScanning]);

  useEffect(() => {
    if (!isScanning || !selectedCameraId || !scannerContainerRef.current)
      return;

    const startScanner = async () => {
      await stopScanner();
      scannerRef.current = new Html5Qrcode('qr-scanner');
      try {
        await scannerRef.current.start(
          selectedCameraId,
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            handleQrScan(decodedText);
          },
          (errorMessage) => {
            console.warn('QR Scan Error:', errorMessage);
          }
        );
        isScannerRunning.current = true;
      } catch (err) {
        console.error('Scanner start error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Scanner Error',
          text: 'Failed to start QR scanner. Please try again.',
          confirmButtonText: 'OK',
        });
        setIsScanning(false);
      }
    };

    startScanner();

    return () => {
      stopScanner();
    };
  }, [selectedCameraId, isScanning]);

  const handleQrScan = (decodedText) => {
    if (!decodedText || currentFishermanIndex === null) return;

    try {
      const [id, name] = decodedText.split(',').map((item) => item.trim());

      const existingFisherman =
        formik.values.fisherman1_id === id ||
        formik.values.fisherman2_id === id ||
        formik.values.fisherman3_id === id;

      if (existingFisherman) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Fisherman',
          text: 'This fisherman has already been assigned.',
          showConfirmButton: false,
          timer: 1500,
          position: 'top-end',
        });
        return;
      }

      formik.setFieldValue(`fisherman${currentFishermanIndex}`, name);
      formik.setFieldValue(`fisherman${currentFishermanIndex}_id`, id);

      stopScanner();
      setIsScanning(false);
      setCurrentFishermanIndex(null);
    } catch (error) {
      console.error('QR parse error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Invalid QR Code',
        text: 'Please scan a valid fisherman QR code.',
        confirmButtonText: 'OK',
      });
    }
  };

  const openQrScanner = async (index) => {
    await cancelScanning();
    setCurrentFishermanIndex(index);
    setIsScanning(true);
  };

  const cancelScanning = async () => {
    await stopScanner();
    setIsScanning(false);
    setCurrentFishermanIndex(null);
    setCameraStatus('Checking camera...');
    setSelectedCameraId('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
  };

  const handleChangeVesselNumber = (selectedOption) => {
    formik.setFieldValue(
      'vesselNumber',
      selectedOption ? selectedOption.value : ''
    );
  };

  const handleChangeHarbour = (selectedOption) => {
    formik.setFieldValue('harbour', selectedOption ? selectedOption.value : '');
  };

  const handleDateChange = async (event) => {
    const selectedDate = event.target.value;
    formik.setFieldValue('requestDate', selectedDate);
    setShouldDisable(false);
  };

  const handleChangeOfficer = async (selectedOption) => {
    formik.setFieldValue('officer', selectedOption ? selectedOption.value : '');
    setShouldDisableTimeSlot(false);

    try {
      const response = await getOccupiedSlots(
        formik.values.requestDate,
        selectedOption ? selectedOption.value : ''
      );
      const occupiedSlots = response.data.timeSlots;
      const updatedSlots = timeSlots.filter(
        (slot) => !occupiedSlots.includes(slot)
      );

      if (updatedSlots.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Time Slots',
          text: 'There are no available time slots left for this date',
          confirmButtonText: 'OK',
        });
      }

      setAvailableTimeSlots(updatedSlots);
    } catch (error) {
      console.error('Error fetching occupied time slots:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch available time slots',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleSubmit = async (formData) => {
    try {
      //setCofSubmitted(true);
      console.log('Submitting COF request:', formData);
      const response = await cofRequest(formData);

      console.log('COF request response:', response);
      await sendNotificationOutside(formData.vesselNumber, formData);
      return response;
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit1: () => {
      return new Promise((resolve, reject) => {
        formik.validateForm().then(async (errors) => {
          if (Object.keys(errors).length === 0) {
            try {
              setIsSubmitting(true);
              //setCofSubmitted(true);

              const selectedOfficer = officers.find(
                (option) => option.value === formik.values.officer
              );
              const formData = {
                ...formik.values,
                officerName: selectedOfficer ? selectedOfficer.label : '',
              };
              const response = await cofRequest(formData);
              await sendNotificationOutside(formData.vesselNumber, formData);
              formik.resetForm();
              resolve(response);
            } catch (error) {
              reject(error);
            } finally {
              setIsSubmitting(false);
            }
          } else {
            reject(new Error('Form validation failed'));
          }
        });
      });
    },
  }));

  const sendNotificationOutside = async (vesselNumber, formData) => {
    const vessel = vesselNumber;
    const requestNotificationPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const token = await getToken(messaging, {
            vapidKey:
              'BBWp8NC-KxklYuyVfI8qI0CDfjEnFgHcsTK0QUD7qYjwMI5Q1hFFS5B5WFP1N1dvv9A-APZRhQog05Nrlo_tRhM',
          });
          if (token) {
            await registerDevice(token);
            await sendNotification('COF', 'COF Requested for ' + vessel, [
              formData.boatOwner,
              formData.officer,
            ]);
          } else {
            console.error('No FCM token found.');
          }
        } else {
          console.log('Notification permission denied.');
        }
      } catch (error) {
        console.error('Error getting notification permission or token:', error);
      }
    };

    const registerDevice = async (deviceId) => {
      try {
        const response = await fetch(
          'https://api.starmarketapi.online/login_firebase/register-device',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ deviceId }),
          }
        );
        await response.json();
      } catch (error) {
        console.error('Error registering device:', error);
      }
    };

    const sendNotification = async (title, body, receivers) => {
      try {
        const notificationData = {
          title,
          body,
          receivers,
        };
        await axios.post(
          'https://api.starmarketapi.online/notifications/send-notification',
          notificationData
        );
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    };

    await requestNotificationPermission();
  };

  return (
    <React.Fragment>
      <Container>
        <Col md={12}>
          <Form onSubmit={formik.handleSubmit}>
            <Input
              type="hidden"
              name="boatOwner"
              value={formik.values.boatOwner}
            />

            <FormGroup>
              <Label for="requestDate">Select Date *</Label>
              <Input
                type="date"
                id="requestDate"
                name="requestDate"
                value={formik.values.requestDate}
                onChange={handleDateChange}
                min={today}
                required
                disabled={isSubmitting}
              />
              {cofSubmitted && formik.errors.requestDate && (
                <div className="invalid-feedback d-block">
                  {formik.errors.requestDate}
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="officer">Select Officer *</Label>
              <Select
                options={officers}
                onChange={handleChangeOfficer}
                value={
                  officers.find(
                    (option) => option.value === formik.values.officer
                  ) || null
                }
                placeholder="Select an officer"
                isClearable
                isSearchable
                isDisabled={shouldDisable || isSubmitting}
              />
              {cofSubmitted && formik.errors.officer && (
                <div className="invalid-feedback d-block">
                  {formik.errors.officer}
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="timeSlot">Select Time Slot *</Label>
              <Input
                type="select"
                id="timeSlot"
                name="timeSlot"
                value={formik.values.timeSlot}
                onChange={formik.handleChange}
                required
                disabled={shouldDisableTimeSlot || isSubmitting}
              >
                <option
                  value=""
                  disabled
                >
                  Choose a time slot
                </option>
                {availableTimeSlots.map((slot, index) => (
                  <option
                    key={index}
                    value={slot}
                  >
                    {slot}
                  </option>
                ))}
              </Input>
              {cofSubmitted && formik.errors.timeSlot && (
                <div className="invalid-feedback d-block">
                  {formik.errors.timeSlot}
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="vesselNumber">Select Vessel *</Label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={options}
                onChange={handleChangeVesselNumber}
                value={
                  options.find(
                    (option) => option.value === formik.values.vesselNumber
                  ) || null
                }
                placeholder="Select a vessel"
                isClearable
                isDisabled={isSubmitting}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    '&:hover': {
                      borderColor: '#80bdff',
                      boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)',
                    },
                  }),
                }}
              />
              {cofSubmitted && formik.errors.vesselNumber && (
                <div className="invalid-feedback d-block">
                  {formik.errors.vesselNumber}
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="harbour">Select Harbour *</Label>
              <Select
                options={harbours}
                onChange={handleChangeHarbour}
                value={
                  harbours.find(
                    (option) => option.value === formik.values.harbour
                  ) || null
                }
                placeholder="Select a harbour"
                isClearable
                isSearchable
                isDisabled={isSubmitting}
              />
              {cofSubmitted && formik.errors.harbour && (
                <div className="invalid-feedback d-block">
                  {formik.errors.harbour}
                </div>
              )}
            </FormGroup>

            {[1, 2, 3].map((index) => (
              <FormGroup
                key={index}
                className="d-flex align-items-center"
              >
                <Col md={4}>
                  <Label
                    for={`fisherman${index}`}
                    className="me-2"
                  >
                    Fisherman {index} {index === 1 && '*'}
                  </Label>
                </Col>
                <Col md={6}>
                  <Input
                    type="text"
                    id={`fisherman${index}`}
                    name={`fisherman${index}`}
                    value={formik.values[`fisherman${index}`] || ''}
                    disabled
                    placeholder="Fisherman Name"
                  />
                  {cofSubmitted && formik.errors[`fisherman${index}`] && (
                    <div className="invalid-feedback d-block">
                      {formik.errors[`fisherman${index}`]}
                    </div>
                  )}
                </Col>
                <Col md={2}>
                  <Button
                    color="primary"
                    size="sm"
                    className="ms-2"
                    onClick={() => openQrScanner(index)}
                    disabled={isScanning || isSubmitting}
                  >
                    <FontAwesomeIcon
                      icon={faQrcode}
                      size="lg"
                    />
                  </Button>
                </Col>
                <Input
                  type="hidden"
                  id={`fisherman${index}_id`}
                  name={`fisherman${index}_id`}
                  value={formik.values[`fisherman${index}_id`] || ''}
                />
              </FormGroup>
            ))}
          </Form>
        </Col>

        <Modal
          isOpen={isScanning}
          toggle={cancelScanning}
        >
          <ModalHeader toggle={cancelScanning}>
            Scan Fisherman {currentFishermanIndex} QR Code
          </ModalHeader>
          <ModalBody>
            <div
              id="qr-scanner"
              ref={scannerContainerRef}
              style={{ width: '100%', minHeight: '300px' }}
            />
            <div className="text-muted text-center mt-2">{cameraStatus}</div>

            {cameras.length > 1 && (
              <FormGroup className="mt-3">
                <Label for="camera-select">Select Camera</Label>
                <Input
                  type="select"
                  id="camera-select"
                  value={selectedCameraId}
                  onChange={(e) => setSelectedCameraId(e.target.value)}
                  disabled={isSubmitting}
                >
                  {cameras.map((camera) => (
                    <option
                      key={camera.id}
                      value={camera.id}
                    >
                      {camera.label || `Camera ${camera.id}`}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={cancelScanning}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </React.Fragment>
  );
});

export default COFRequest;
